import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "./Pengumuman";
import { ShieldCheck, Sun, Moon, Edit3, RefreshCw, Users as UsersIcon, X, FileText, FileSpreadsheet, FileDown, ClipboardList, CircleDollarSign, Loader2, Check, Wallet, ReceiptText } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useApp } from "../context/AppContext";
import { satpamAPI, satpamPayrollAPI, kasbonSatpamAPI, settingsAPI } from "../lib/api";
import { exportToExcel, exportToPDF, exportToWord } from "../lib/exportUtils";
import LaporanKegiatanDialog from "../components/LaporanKegiatanDialog";
import { getWIBParts } from "../components/WIBClock";
import { toast } from "sonner";

const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const HARI_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

const MONTH_OPTIONS = (() => {
  const arr = [];
  // Aug 2025 - Dec 2026
  for (let m = 8; m <= 12; m++) arr.push({ value: `2025-${String(m).padStart(2, "0")}`, label: `${BULAN_ID[m - 1]} 2025` });
  for (let m = 1; m <= 12; m++) arr.push({ value: `2026-${String(m).padStart(2, "0")}`, label: `${BULAN_ID[m - 1]} 2026` });
  return arr;
})();

const currentMonthValue = () => {
  const p = getWIBParts();
  const mIdx = BULAN_ID.indexOf(p.bulan) + 1;
  return `${p.tahun}-${String(mIdx).padStart(2, "0")}`;
};

const parseTanggal = (iso) => {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d);
  return {
    hari: HARI_ID[dt.getDay()],
    tanggalNum: d,
    bulan: BULAN_ID[m - 1],
    tahun: y,
    dow: dt.getDay(),
  };
};

const GroupColor = { G1: "bg-emerald-100 text-emerald-800 border-emerald-200", G2: "bg-blue-100 text-blue-800 border-blue-200", G3: "bg-amber-100 text-amber-800 border-amber-200" };

const JadwalSatpam = () => {
  const { user, theme, updateTheme } = useApp();
  const canEdit = user.role === "admin" || user.role === "super_admin";
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState({});
  const [bulan, setBulan] = useState(() => {
    const cur = currentMonthValue();
    return MONTH_OPTIONS.find((m) => m.value === cur) ? cur : "2025-08";
  });
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [lapPrefill, setLapPrefill] = useState(null);

  const load = () => {
    setLoading(true);
    satpamAPI.list(bulan).then((rows) => { setItems(rows); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { satpamAPI.groups().then(setGroups).catch(() => {}); }, []);
  useEffect(() => { load(); }, [bulan]);

  // Group by date
  const grouped = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!map[it.tanggal]) map[it.tanggal] = { pagi: null, malam: null };
      map[it.tanggal][it.shift] = it;
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  // Stats
  const stats = useMemo(() => {
    const counts = { G1: 0, G2: 0, G3: 0 };
    for (const it of items) counts[it.group_key] = (counts[it.group_key] || 0) + 1;
    return counts;
  }, [items]);

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      group_key: row.group_key,
      personel1: row.personel[0] || "",
      personel2: row.personel[1] || "",
      catatan: row.catatan || "",
    });
    setEditOpen(true);
  };

  const applyGroup = (key) => {
    if (!groups[key]) return;
    setForm((f) => ({ ...f, group_key: key, personel1: groups[key].personel[0], personel2: groups[key].personel[1] }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        group_key: form.group_key,
        group_name: groups[form.group_key]?.nama || editing.group_name,
        personel: [form.personel1, form.personel2].filter(Boolean),
        catatan: form.catatan || "",
      };
      await satpamAPI.update(editing.id, payload);
      toast.success("Jadwal diperbarui");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Ini akan MENGHAPUS semua jadwal dan membuat ulang (Agu 2025 - Des 2026). Lanjutkan?")) return;
    try {
      const res = await satpamAPI.generate();
      toast.success(`Berhasil generate ${res.count} shift`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const SHIFT_ORDER = { pagi: 0, malam: 1 };
  const bulanLabel = MONTH_OPTIONS.find((m) => m.value === bulan)?.label || bulan;
  const doExport = (kind) => {
    if (!items.length) { toast.error("Tidak ada jadwal untuk bulan ini"); return; }
    const sorted = [...items].sort((a, b) => a.tanggal.localeCompare(b.tanggal) || (SHIFT_ORDER[a.shift] ?? 9) - (SHIFT_ORDER[b.shift] ?? 9));
    const headers = ["Tanggal", "Hari", "Shift", "Jam", "Grup", "Personel", "Catatan"];
    const rows = sorted.map((s) => {
      const t = parseTanggal(s.tanggal);
      return [s.tanggal, t.hari, s.shift.toUpperCase(), s.jam, s.group_name || s.group_key, (s.personel || []).join(", "), s.catatan || ""];
    });
    const summary = [
      ["Bulan", bulanLabel],
      ["Total Shift", String(items.length)],
      ...["G1", "G2", "G3"].map((k) => [groups[k]?.nama || k, `${stats[k] || 0} shift`]),
    ];
    const title = `Jadwal Satpam — ${bulanLabel}`;
    const subtitle = "Rotasi 3 grup x 2 shift (Pagi/Malam) • Bahan laporan & pembayaran gaji • Warga DM Residence Garut";
    if (kind === "xlsx") exportToExcel({ filename: `jadwal-satpam-${bulan}.xlsx`, sheetName: `Satpam ${bulanLabel}`, headers, rows, summaryRows: summary });
    else if (kind === "pdf") exportToPDF({ filename: `jadwal-satpam-${bulan}.pdf`, title, subtitle, headers, rows, summary });
    else exportToWord({ filename: `jadwal-satpam-${bulan}.doc`, title, subtitle, headers, rows, summary });
    toast.success(`File ${kind.toUpperCase()} berhasil diunduh`);
  };

  return (
    <div>
      <PageHeader title="Jadwal Satpam" subtitle="Rotasi 3 grup × 2 shift (Pagi/Malam) di DM Residence" icon={ShieldCheck}
        action={user.role === "super_admin" && <Button variant="outline" onClick={regenerate}><RefreshCw className="h-4 w-4 mr-1" />Generate Ulang</Button>} />

      {/* Group info cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-5">
        {["G1", "G2", "G3"].map((k) => {
          const g = groups[k];
          if (!g) return null;
          return (
            <Card key={k} className={`border-2 ${k === "G1" ? "border-emerald-200 bg-emerald-50/40" : k === "G2" ? "border-blue-200 bg-blue-50/40" : "border-amber-200 bg-amber-50/40"}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`h-9 w-9 rounded-lg grid place-items-center font-bold ${k === "G1" ? "bg-emerald-600 text-white" : k === "G2" ? "bg-blue-600 text-white" : "bg-amber-500 text-white"}`}>{k}</div>
                    <div className="font-bold text-slate-800">{g.nama}</div>
                  </div>
                  <Badge variant="secondary" className="bg-white/70">{stats[k] || 0} shift</Badge>
                </div>
                <div className="text-sm text-slate-600 space-y-1">
                  {g.personel.map((p) => (<div key={p} className="flex items-center gap-2"><UsersIcon className="h-3.5 w-3.5 text-slate-400" />{p}</div>))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Label className="text-sm">Bulan:</Label>
        <Select value={bulan} onValueChange={setBulan}>
          <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[300px]">{MONTH_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="text-xs text-slate-500">{grouped.length} hari, {items.length} shift</div>
        <div className="flex gap-1.5 ml-auto items-center" data-testid="satpam-export-bar">
          <span className="text-xs font-bold text-slate-700 hidden sm:inline">Unduh:</span>
          <Button variant="outline" size="sm" onClick={() => doExport("pdf")} data-testid="satpam-export-pdf"><FileText className="h-3.5 w-3.5 mr-1 text-rose-600" />PDF</Button>
          <Button variant="outline" size="sm" onClick={() => doExport("docx")} data-testid="satpam-export-docx"><FileDown className="h-3.5 w-3.5 mr-1 text-blue-600" />Word</Button>
          <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid="satpam-export-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-1 text-emerald-600" />Excel</Button>
        </div>
      </div>

      {loading && <div className="text-center py-8 text-slate-400">Memuat...</div>}

      <div className="space-y-3">
        {grouped.map(([tanggal, shifts]) => {
          const t = parseTanggal(tanggal);
          const isWeekend = t.dow === 0 || t.dow === 6;
          return (
            <Card key={tanggal} className={`${isWeekend ? "border-amber-200 bg-amber-50/20" : "border-slate-200"} overflow-hidden`}>
              <CardContent className="p-0">
                <div className="grid md:grid-cols-[140px_1fr_1fr] items-stretch">
                  <div className={`p-4 flex flex-col justify-center items-center text-center ${isWeekend ? "bg-amber-100/50" : "bg-slate-50"}`}>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.hari}</div>
                    <div className="text-3xl font-extrabold brand-text" style={{ fontFamily: "Poppins, sans-serif" }}>{t.tanggalNum}</div>
                    <div className="text-xs text-slate-500">{t.bulan} {t.tahun}</div>
                  </div>
                  {["pagi", "malam"].map((st) => {
                    const s = shifts[st];
                    if (!s) return <div key={st} className="p-4 text-slate-400 text-sm">-</div>;
                    return (
                      <div key={st} className={`p-4 border-l border-slate-100 ${st === "pagi" ? "" : "bg-slate-50/40"}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {st === "pagi" ? <Sun className="h-4 w-4 text-amber-500" /> : <Moon className="h-4 w-4 text-indigo-500" />}
                            <div className="font-bold text-sm uppercase text-slate-700">{st}</div>
                            <div className="text-xs text-slate-500">{s.jam} WIB</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${GroupColor[s.group_key] || ""}`}>{s.group_name}</span>
                            {canEdit && <button onClick={() => setLapPrefill({ tanggal, kategori: "satpam", petugas_nama: (s.personel || []).join(" & ") || s.group_name, jam: s.jam })} className="p-1 hover:bg-emerald-50 rounded" title="Buat Laporan Kegiatan" data-testid={`laporan-btn-${tanggal}-${st}`}><ClipboardList className="h-3.5 w-3.5 text-emerald-600" /></button>}
                            {canEdit && <button onClick={() => openEdit(s)} className="p-1 hover:bg-slate-100 rounded"><Edit3 className="h-3.5 w-3.5 text-slate-500" /></button>}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(s.personel || []).map((p) => (<Badge key={p} variant="secondary" className="bg-white border text-slate-700">{p}</Badge>))}
                        </div>
                        {s.catatan && <div className="text-xs text-slate-500 mt-2 italic">{s.catatan}</div>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {!loading && grouped.length === 0 && <div className="text-center py-10 text-slate-400">Belum ada jadwal untuk bulan ini</div>}
      </div>

      <SatpamPayroll bulan={bulan} bulanLabel={bulanLabel} canEdit={canEdit} theme={theme} updateTheme={updateTheme} />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Jadwal — {editing ? `${editing.shift.toUpperCase()} ${editing.tanggal}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-2 block">Pilih Group (mengisi otomatis personel)</Label>
              <div className="grid grid-cols-3 gap-2">
                {["G1", "G2", "G3"].map((k) => (
                  <button key={k} onClick={() => applyGroup(k)} className={`p-2 rounded-lg border-2 text-left text-xs ${form.group_key === k ? "border-emerald-500 shadow" : "border-slate-200 hover:border-emerald-300"}`}>
                    <div className="font-bold text-slate-800">{groups[k]?.nama || k}</div>
                    <div className="text-slate-500">{(groups[k]?.personel || []).join(", ")}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Personel 1</Label><Input value={form.personel1 || ""} onChange={(e) => setForm({ ...form, personel1: e.target.value })} /></div>
              <div><Label>Personel 2</Label><Input value={form.personel2 || ""} onChange={(e) => setForm({ ...form, personel2: e.target.value })} /></div>
            </div>
            <div><Label>Catatan</Label><Textarea rows={2} value={form.catatan || ""} onChange={(e) => setForm({ ...form, catatan: e.target.value })} placeholder="Contoh: Mang Iki cuti, digantikan Fadil" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}><X className="h-4 w-4 mr-1" />Batal</Button><Button onClick={saveEdit} className="brand-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <LaporanKegiatanDialog open={!!lapPrefill} onOpenChange={() => setLapPrefill(null)} prefill={lapPrefill} />
    </div>
  );
};

const formatIDRp = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const loadImageDataUrl = (url) => new Promise((resolve) => {
  if (!url) return resolve(null);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png"));
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

const SatpamPayroll = ({ bulan, bulanLabel, canEdit, theme, updateTheme }) => {
  const [payroll, setPayroll] = useState(null);
  const [loadingP, setLoadingP] = useState(false);
  const [rates, setRates] = useState({ pagi: theme?.upahSatpamPagi ?? 50000, malam: theme?.upahSatpamMalam ?? 65000, bonus: theme?.bonusKomandanSatpam ?? 300000 });
  const [savingRates, setSavingRates] = useState(false);
  const [kasbonFor, setKasbonFor] = useState(null);
  const [kasbonList, setKasbonList] = useState([]);
  const [kasbonForm, setKasbonForm] = useState({ jumlah: "", keterangan: "" });
  const [savingKasbon, setSavingKasbon] = useState(false);
  const [slipFor, setSlipFor] = useState(null);

  useEffect(() => {
    setRates({ pagi: theme?.upahSatpamPagi ?? 50000, malam: theme?.upahSatpamMalam ?? 65000, bonus: theme?.bonusKomandanSatpam ?? 300000 });
  }, [theme?.upahSatpamPagi, theme?.upahSatpamMalam, theme?.bonusKomandanSatpam]);

  const load = () => {
    setLoadingP(true);
    satpamPayrollAPI.get(bulan).then(setPayroll).catch(() => setPayroll(null)).finally(() => setLoadingP(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bulan]);

  const ratesDirty = payroll && (parseInt(rates.pagi) !== payroll.upah_satpam_pagi || parseInt(rates.malam) !== payroll.upah_satpam_malam || parseInt(rates.bonus) !== payroll.bonus_komandan);

  const saveRates = async () => {
    setSavingRates(true);
    try {
      const payload = { upahSatpamPagi: parseInt(rates.pagi) || 0, upahSatpamMalam: parseInt(rates.malam) || 0, bonusKomandanSatpam: parseInt(rates.bonus) || 0 };
      await settingsAPI.update(payload);
      await updateTheme(payload);
      toast.success("Tarif upah diperbarui"); load();
    } catch { toast.error("Gagal simpan tarif"); }
    finally { setSavingRates(false); }
  };

  const refreshKasbonList = (nama) => kasbonSatpamAPI.list(bulan).then((l) => setKasbonList(l.filter((k) => k.personel === nama))).catch(() => {});
  const openKasbon = (nama) => { setKasbonFor(nama); setKasbonForm({ jumlah: "", keterangan: "" }); refreshKasbonList(nama); };
  const addKasbon = async () => {
    const jml = parseInt(kasbonForm.jumlah) || 0;
    if (jml <= 0) { toast.error("Jumlah kasbon harus lebih dari 0"); return; }
    setSavingKasbon(true);
    try {
      await kasbonSatpamAPI.add({ bulan, personel: kasbonFor, jumlah: jml, keterangan: kasbonForm.keterangan });
      toast.success("Kasbon dicatat — otomatis memotong gaji");
      setKasbonForm({ jumlah: "", keterangan: "" });
      refreshKasbonList(kasbonFor); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setSavingKasbon(false); }
  };
  const delKasbon = async (id) => {
    if (!window.confirm("Hapus kasbon ini?")) return;
    try { await kasbonSatpamAPI.remove(id); toast.success("Dihapus"); refreshKasbonList(kasbonFor); load(); }
    catch { toast.error("Gagal"); }
  };

  const logoAbs = theme?.logo ? (theme.logo.startsWith("http") ? theme.logo : `${window.location.origin}${theme.logo}`) : "";

  const slipData = (p) => ({
    headers: ["Uraian", "Perhitungan", "Jumlah"],
    rows: [
      ["Shift Pagi", `${p.shift_pagi} shift × ${formatIDRp(payroll.upah_satpam_pagi)}`, formatIDRp(p.upah_pagi)],
      ["Shift Malam", `${p.shift_malam} shift × ${formatIDRp(payroll.upah_satpam_malam)}`, formatIDRp(p.upah_malam)],
      ["Tunjangan Komandan Regu", p.is_komandan ? "Komandan Regu Satpam" : "-", p.bonus ? formatIDRp(p.bonus) : "-"],
      ["TOTAL UPAH", "", formatIDRp(p.total)],
      ["Potongan Kasbon", "", p.kasbon ? `- ${formatIDRp(p.kasbon)}` : "-"],
      ["GAJI BERSIH (SISA)", "", formatIDRp(p.sisa)],
    ],
    summary: [["Nama", p.nama], ["Jabatan", p.is_komandan ? "Komandan Regu" : "Anggota"], ["Grup", p.group_key || "-"], ["Periode", bulanLabel]],
  });

  const downloadSlip = async (p, kind) => {
    const { headers, rows, summary } = slipData(p);
    const title = `SLIP GAJI SATPAM — ${p.nama}`;
    const subtitle = `Periode ${bulanLabel} • Warga RT DM Residence Garut`;
    const safe = p.nama.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    if (kind === "xlsx") exportToExcel({ filename: `slip-gaji-${safe}-${bulan}.xlsx`, sheetName: `Slip ${p.nama}`.slice(0, 31), headers, rows, summaryRows: summary });
    else if (kind === "pdf") { const logo = await loadImageDataUrl(logoAbs); exportToPDF({ filename: `slip-gaji-${safe}-${bulan}.pdf`, title, subtitle, headers, rows, summary, orientation: "portrait", logoDataUrl: logo || "" }); }
    else exportToWord({ filename: `slip-gaji-${safe}-${bulan}.doc`, title, subtitle, headers, rows, summary, logoUrl: logoAbs });
    toast.success(`Slip gaji ${kind.toUpperCase()} diunduh`);
  };

  const downloadRincian = async (kind) => {
    if (!payroll?.items?.length) { toast.error("Tidak ada data"); return; }
    const moneyCols = [3, 5, 6, 7, 8, 9];
    const headers = ["Personel", "Grup", "Shift Pagi", "Upah Pagi", "Shift Malam", "Upah Malam", "Bonus Komandan", "Total Upah", "Potongan Kasbon", "Sisa (Gaji Bersih)"];
    const rows = payroll.items.map((p) => [p.nama + (p.is_komandan ? " (Komandan)" : ""), p.group_key || "-", p.shift_pagi, p.upah_pagi, p.shift_malam, p.upah_malam, p.bonus, p.total, p.kasbon, p.sisa]);
    const summary = [
      ["Periode", bulanLabel],
      ["Tarif Pagi / Malam", `${formatIDRp(payroll.upah_satpam_pagi)} / ${formatIDRp(payroll.upah_satpam_malam)}`],
      ["Bonus Komandan Regu", formatIDRp(payroll.bonus_komandan)],
      ["TOTAL UPAH", formatIDRp(payroll.grand_total)],
      ["TOTAL POTONGAN KASBON", formatIDRp(payroll.grand_kasbon)],
      ["TOTAL SISA (DIBAYARKAN)", formatIDRp(payroll.grand_sisa)],
    ];
    const title = `Rincian Upah Satpam — ${bulanLabel}`;
    const subtitle = "Perhitungan: jumlah shift × tarif + bonus komandan − kasbon • Warga RT DM Residence Garut";
    if (kind === "xlsx") exportToExcel({ filename: `rincian-upah-satpam-${bulan}.xlsx`, sheetName: "Rincian Upah Satpam", headers, rows, summaryRows: summary });
    else if (kind === "pdf") { const logo = await loadImageDataUrl(logoAbs); exportToPDF({ filename: `rincian-upah-satpam-${bulan}.pdf`, title, subtitle, headers, rows, summary, orientation: "landscape", numericCols: moneyCols, logoDataUrl: logo || "" }); }
    else exportToWord({ filename: `rincian-upah-satpam-${bulan}.doc`, title, subtitle, headers, rows: rows.map((r) => r.map((c, ci) => (typeof c === "number" && moneyCols.includes(ci) ? formatIDRp(c) : c))), summary, logoUrl: logoAbs });
    toast.success(`Rincian ${kind.toUpperCase()} diunduh`);
  };

  return (
    <Card className="border-emerald-200 mt-2" data-testid="satpam-payroll-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="h-9 w-9 rounded-lg bg-emerald-100 grid place-items-center"><CircleDollarSign className="h-5 w-5 text-emerald-700" /></div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Perhitungan Upah Satpam — {bulanLabel}</h3>
            <p className="text-xs text-slate-500">Jumlah shift × tarif + tunjangan Komandan Regu − potongan kasbon</p>
          </div>
          {canEdit && (
            <div className="flex gap-1.5 items-center" data-testid="payroll-export-bar">
              <span className="text-xs font-bold text-slate-700 hidden sm:inline">Rincian:</span>
              <Button variant="outline" size="sm" onClick={() => downloadRincian("pdf")} data-testid="payroll-export-pdf"><FileText className="h-3.5 w-3.5 mr-1 text-rose-600" />PDF</Button>
              <Button variant="outline" size="sm" onClick={() => downloadRincian("docx")} data-testid="payroll-export-docx"><FileDown className="h-3.5 w-3.5 mr-1 text-blue-600" />Word</Button>
              <Button variant="outline" size="sm" onClick={() => downloadRincian("xlsx")} data-testid="payroll-export-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-1 text-emerald-600" />Excel</Button>
            </div>
          )}
        </div>

        {/* Tarif Editable */}
        <div className="rounded-xl border-2 border-amber-200 bg-amber-50/60 p-3 flex items-center gap-3 flex-wrap">
          <Wallet className="h-5 w-5 text-amber-700" />
          <div className="flex items-center gap-1.5">
            <Label className="text-xs font-bold text-amber-900 whitespace-nowrap">Upah Pagi:</Label>
            <Input type="number" value={rates.pagi} onChange={(e) => setRates({ ...rates, pagi: e.target.value })} className="w-32 tabular-nums font-mono h-8" disabled={!canEdit} data-testid="payroll-rate-pagi" />
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs font-bold text-amber-900 whitespace-nowrap">Upah Malam:</Label>
            <Input type="number" value={rates.malam} onChange={(e) => setRates({ ...rates, malam: e.target.value })} className="w-32 tabular-nums font-mono h-8" disabled={!canEdit} data-testid="payroll-rate-malam" />
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs font-bold text-amber-900 whitespace-nowrap">Bonus Komandan/bln:</Label>
            <Input type="number" value={rates.bonus} onChange={(e) => setRates({ ...rates, bonus: e.target.value })} className="w-32 tabular-nums font-mono h-8" disabled={!canEdit} data-testid="payroll-rate-bonus" />
          </div>
          {canEdit && ratesDirty && (
            <Button size="sm" onClick={saveRates} disabled={savingRates} className="brand-btn h-8" data-testid="payroll-rates-save">
              {savingRates ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Menyimpan...</> : <><Check className="h-3 w-3 mr-1" />Simpan Tarif</>}
            </Button>
          )}
        </div>

        {loadingP ? (
          <div className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-1" />Menghitung upah...</div>
        ) : !payroll?.items?.length ? (
          <div className="text-center py-8 text-slate-400 text-sm">Belum ada data personel/jadwal untuk bulan ini.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-sm">
              <thead className="bg-emerald-700 text-white">
                <tr>
                  <th className="p-2 text-left">Personel</th>
                  <th className="p-2 text-center">Pagi</th>
                  <th className="p-2 text-center">Malam</th>
                  <th className="p-2 text-right">Bonus</th>
                  <th className="p-2 text-right">Total Upah</th>
                  <th className="p-2 text-right">Kasbon</th>
                  <th className="p-2 text-right">Sisa (Gaji Bersih)</th>
                  {canEdit && <th className="p-2 text-right">Aksi</th>}
                </tr>
              </thead>
              <tbody data-testid="payroll-table">
                {payroll.items.map((p) => (
                  <tr key={p.nama} className="border-t border-emerald-100">
                    <td className="p-2">
                      <div className="font-bold text-slate-800">{p.nama}</div>
                      <div className="flex gap-1 mt-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${GroupColor[p.group_key] || ""}`}>{p.group_key || "-"}</span>
                        {p.is_komandan && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[9px]">KOMANDAN REGU</Badge>}
                      </div>
                    </td>
                    <td className="p-2 text-center whitespace-nowrap"><div className="font-bold tabular-nums">{p.shift_pagi}×</div><div className="text-[11px] text-slate-500 tabular-nums">{formatIDRp(p.upah_pagi)}</div></td>
                    <td className="p-2 text-center whitespace-nowrap"><div className="font-bold tabular-nums">{p.shift_malam}×</div><div className="text-[11px] text-slate-500 tabular-nums">{formatIDRp(p.upah_malam)}</div></td>
                    <td className="p-2 text-right tabular-nums text-amber-700 font-semibold">{p.bonus ? formatIDRp(p.bonus) : "—"}</td>
                    <td className="p-2 text-right tabular-nums font-black text-emerald-700" style={{ fontFamily: "Poppins, sans-serif" }}>{formatIDRp(p.total)}</td>
                    <td className="p-2 text-right tabular-nums text-rose-600 font-semibold">{p.kasbon ? `- ${formatIDRp(p.kasbon)}` : "—"}</td>
                    <td className="p-2 text-right tabular-nums font-black brand-text" style={{ fontFamily: "Poppins, sans-serif" }}>{formatIDRp(p.sisa)}</td>
                    {canEdit && (
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" title="Kelola Kasbon" onClick={() => openKasbon(p.nama)} className="h-7 w-7 p-0 text-rose-600" data-testid={`payroll-kasbon-${p.nama.replace(/\s+/g, "-")}`}><Wallet className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" title="Slip Gaji" onClick={() => setSlipFor(p)} className="h-7 w-7 p-0 text-emerald-700" data-testid={`payroll-slip-${p.nama.replace(/\s+/g, "-")}`}><ReceiptText className="h-3.5 w-3.5" /></Button>
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="border-t-2 border-emerald-300 bg-emerald-50/70 font-bold">
                  <td className="p-2">TOTAL</td>
                  <td className="p-2 text-center tabular-nums">{payroll.items.reduce((a, b) => a + b.shift_pagi, 0)}</td>
                  <td className="p-2 text-center tabular-nums">{payroll.items.reduce((a, b) => a + b.shift_malam, 0)}</td>
                  <td className="p-2 text-right tabular-nums">{formatIDRp(payroll.items.reduce((a, b) => a + b.bonus, 0))}</td>
                  <td className="p-2 text-right tabular-nums text-emerald-800">{formatIDRp(payroll.grand_total)}</td>
                  <td className="p-2 text-right tabular-nums text-rose-700">- {formatIDRp(payroll.grand_kasbon)}</td>
                  <td className="p-2 text-right tabular-nums brand-text">{formatIDRp(payroll.grand_sisa)}</td>
                  {canEdit && <td />}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Dialog Kasbon */}
        <Dialog open={!!kasbonFor} onOpenChange={() => setKasbonFor(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-rose-600" />Kasbon — {kasbonFor} ({bulanLabel})</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {kasbonList.length > 0 && (
                <div className="space-y-1.5" data-testid="kasbon-list">
                  {kasbonList.map((k) => (
                    <div key={k.id} className="flex items-center gap-2 rounded-lg border bg-white p-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-bold text-rose-700 tabular-nums">{formatIDRp(k.jumlah)}</span>
                        {k.keterangan && <span className="text-slate-600"> — {k.keterangan}</span>}
                        <div className="text-[10px] text-slate-400">oleh {k.created_by || "admin"}</div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => delKasbon(k.id)} data-testid={`kasbon-delete-${k.id}`}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Jumlah (Rp) *</Label><Input type="number" value={kasbonForm.jumlah} onChange={(e) => setKasbonForm({ ...kasbonForm, jumlah: e.target.value })} placeholder="100000" data-testid="kasbon-jumlah-input" /></div>
                <div><Label>Keterangan</Label><Input value={kasbonForm.keterangan} onChange={(e) => setKasbonForm({ ...kasbonForm, keterangan: e.target.value })} placeholder="Keperluan..." data-testid="kasbon-keterangan-input" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setKasbonFor(null)}>Tutup</Button>
              <Button className="brand-btn" onClick={addKasbon} disabled={savingKasbon} data-testid="kasbon-add-btn">
                {savingKasbon ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Menyimpan...</> : "Tambah Kasbon"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Slip Gaji */}
        <Dialog open={!!slipFor} onOpenChange={() => setSlipFor(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ReceiptText className="h-5 w-5 text-emerald-700" />Slip Gaji — {slipFor?.nama}</DialogTitle></DialogHeader>
            {slipFor && payroll && (
              <div className="space-y-3">
                <div className="rounded-xl border-2 border-emerald-200 overflow-hidden">
                  <div className="bg-emerald-700 text-white p-3 flex items-center gap-3">
                    {theme?.logo && <img src={theme.logo} alt="logo" className="h-10 w-10 rounded-full object-cover bg-white p-0.5" />}
                    <div>
                      <div className="font-black text-sm" style={{ fontFamily: "Poppins, sans-serif" }}>{theme?.appName || "WARGA RT DM RESIDENCE"}</div>
                      <div className="text-[11px] opacity-90">Slip Gaji Satpam • {bulanLabel}</div>
                    </div>
                  </div>
                  <div className="p-3 bg-white">
                    <table className="w-full text-xs" data-testid="slip-preview">
                      <tbody>
                        {slipData(slipFor).rows.map((r, i) => (
                          <tr key={i} className={`border-b border-slate-100 ${i >= 3 ? "font-bold" : ""} ${i === 5 ? "bg-emerald-50 text-emerald-800" : ""}`}>
                            <td className="py-1.5 pr-2">{r[0]}</td>
                            <td className="py-1.5 pr-2 text-slate-500">{r[1]}</td>
                            <td className="py-1.5 text-right tabular-nums">{r[2]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex gap-2" data-testid="slip-download-bar">
                  <Button className="flex-1 brand-btn" size="sm" onClick={() => downloadSlip(slipFor, "pdf")} data-testid="slip-download-pdf"><FileText className="h-3.5 w-3.5 mr-1" />PDF</Button>
                  <Button className="flex-1 brand-btn" size="sm" onClick={() => downloadSlip(slipFor, "docx")} data-testid="slip-download-docx"><FileDown className="h-3.5 w-3.5 mr-1" />Word</Button>
                  <Button className="flex-1 brand-btn" size="sm" onClick={() => downloadSlip(slipFor, "xlsx")} data-testid="slip-download-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Excel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default JadwalSatpam;
